<template>
  <div class="flex flex-col gap-3">
    <div
      v-for="item in sortedDealerships"
      :key="item.dealership.id"
      class="group-dealership-card"
    >
      <div class="mb-2 flex min-w-0 items-center justify-between gap-4">
        <div class="min-w-0">
          <div class="truncate text-lg font-semibold">{{ item.dealership.name }}</div>
        </div>
        <Chip
          v-tippy="'Appointment-to-deal conversion'"
          :severity="conversionSeverity(conversionRate(item))"
          size="sm"
          class="shrink-0"
        >
          {{ formatPercent(conversionRate(item)) }}
        </Chip>
      </div>

      <div class="grid min-w-0 gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <div
          v-for="detail in dealershipDetails(item)"
          :key="detail.label"
          class="group-dealership-detail"
        >
          <div
            v-if="detail.imageUrl"
            class="flex h-10 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary_alt"
          >
            <Image
              :src="detail.imageUrl"
              :alt="detail.value"
              image-class="h-full w-full object-contain"
            >
              <template #fallbackContent>
                <Avatar
                  icon="car"
                  size="xs"
                  color="brand"
                  no-border
                />
              </template>
            </Image>
          </div>
          <Avatar
            v-else
            :icon="detail.icon"
            :url="detail.avatarUrl"
            :text="detail.avatarText"
            size="xs"
            :color="detail.color"
            no-border
          />
          <div class="min-w-0">
            <div class="truncate text-sm font-semibold">{{ detail.value }}</div>
            <div class="text-xs text-secondary">{{ detail.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'

// components
import Avatar from '@/components/avatar/Avatar.vue'
import Chip from '@/components/chip/Chip.vue'
import Image from '@/components/image/Image.vue'

// types
import type { GroupDashboardDealership } from '@/types/domain/groupDashboard'

// utilities
import { formatCurrency, formatNumber } from '@/utilities/number'

/** Dealership performance rows to render as visual cards. */
const props = defineProps({
  dealerships: {
    type: Array as PropType<GroupDashboardDealership[]>,
    required: true
  }
})

/** Dealerships ordered by conversion rate so strongest converters lead the grid. */
const sortedDealerships = computed(() =>
  [...props.dealerships].sort(
    (a, b) =>
      conversionRate(b) - conversionRate(a) ||
      b.stats.deals - a.stats.deals ||
      b.stats.totalRevenue - a.stats.totalRevenue
  )
)

/** Detail card shown inside a dealership row. */
interface DealershipDetail {
  icon: string
  label: string
  value: string
  avatarUrl?: string
  avatarText?: string
  imageUrl?: string
  color: string
}

/**
 * Calculates appointment-to-deal conversion for a dealership.
 * @param item - Dealership dashboard row.
 * @returns Decimal conversion rate.
 */
function conversionRate(item: GroupDashboardDealership): number {
  // Avoid division by zero when no appointments occurred.
  if (item.stats.appointmentsOccurring === 0) {
    return 0
  }

  return item.stats.deals / item.stats.appointmentsOccurring
}

/**
 * Gets the RAG chip severity for a conversion rate.
 * @param rate - Decimal conversion rate.
 * @returns Chip severity for RAG display.
 */
function conversionSeverity(rate: number): 'success' | 'warning' | 'danger' {
  // Green for dealerships converting at 30% or above.
  if (rate >= 0.3) {
    return 'success'
  }

  // Amber for dealerships converting from 20% up to 30%.
  if (rate >= 0.2) {
    return 'warning'
  }

  return 'danger'
}

/**
 * Builds compact detail cards for a dealership row.
 * @param item - Dealership dashboard row.
 * @returns Dealership detail card definitions.
 */
function dealershipDetails(item: GroupDashboardDealership): DealershipDetail[] {
  return [
    {
      icon: 'sterling-sign',
      label: 'Revenue',
      value: formatCurrency(item.stats.totalRevenue, 'GBP', 0) ?? '£0',
      color: 'secondary'
    },
    {
      icon: 'calendar-plus',
      label: 'Booked',
      value: formatNumberValue(item.stats.appointmentsBooked),
      color: 'secondary'
    },
    {
      icon: 'calendar-check',
      label: 'Occurred',
      value: formatNumberValue(item.stats.appointmentsOccurring),
      color: 'secondary'
    },
    {
      icon: 'user',
      label: 'Top salesperson',
      value: item.stats.topSalesPerson?.user?.name ?? 'No sales yet',
      avatarUrl: item.stats.topSalesPerson?.user?.profilePicUrl ?? undefined,
      avatarText: item.stats.topSalesPerson?.user?.initials,
      color: 'secondary'
    },
    {
      icon: 'car',
      label: 'Most sold',
      value: vehicleName(item),
      imageUrl: vehicleImageUrl(item),
      color: 'brand'
    }
  ]
}

/**
 * Formats a decimal as a percentage.
 * @param value - Decimal value.
 * @returns Percentage label.
 */
function formatPercent(value: number): string {
  return `${formatNumber(value * 100, 'normal', 1) ?? '0'}%`
}

/**
 * Formats a normal number for display.
 * @param value - Numeric value.
 * @returns Formatted number label.
 */
function formatNumberValue(value: number): string {
  return formatNumber(value, 'normal') ?? '0'
}

/**
 * Gets a readable vehicle name for a dealership card.
 * @param item - Dealership dashboard row.
 * @returns Vehicle name or fallback text.
 */
function vehicleName(item: GroupDashboardDealership): string {
  const vehicle = item.stats.mostSoldVehicle

  // Return a fallback when the endpoint has no vehicle winner.
  if (!vehicle?.make && !vehicle?.model) {
    return 'No vehicle winner'
  }

  return [vehicle.make, vehicle.model].filter(Boolean).join(' ')
}

/**
 * Gets the best available image URL for a dealership's top vehicle.
 * @param item - Dealership dashboard row.
 * @returns Vehicle image URL when present.
 */
function vehicleImageUrl(item: GroupDashboardDealership): string | undefined {
  const vehicle = item.stats.mostSoldVehicle

  return vehicle?.imageUrl ?? vehicle?.imageURL ?? vehicle?.vehicleImageUrl ?? vehicle?.image ?? undefined
}
</script>

<style scoped>
.group-dealership-card {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 1.75rem;
  padding: var(--s-3);
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 18px 48px rgba(16, 24, 40, 0.06);
  backdrop-filter: blur(16px);
}

.group-dealership-detail {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: var(--s-2);
  border-radius: 1rem;
  padding: var(--s-2) var(--s-3);
  background: rgba(148, 163, 184, 0.12);
}

.dark .group-dealership-card {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(17, 24, 39, 0.78);
}

.dark .group-dealership-detail {
  background: rgba(255, 255, 255, 0.06);
}
</style>
